/**
 * Insurance Guards
 *
 * Guards for insurance lookup actor results.
 */

import type { Result } from "neverthrow";
import type { HrtType } from "../actors/load-hrts.actor";
import type { GuardWithMetadata } from "./sms.guard";

/**
 * Type-safe result checker
 */
function isResult<T, E>(result: unknown): result is Result<T, E> {
  return (
    result !== null &&
    typeof result === "object" &&
    "isOk" in result &&
    typeof result.isOk === "function"
  );
}

/**
 * Helper to check if all insurances are active based on insuranceStates
 * Replaces the legacy `allActive` context field
 */
// biome-ignore lint/suspicious/noExplicitAny: Context type varies
export function isAtLeastPrimaryInsuranceActive(context: any): boolean {
  const states = context.insuranceStates;
  const primary = states?.primary;
  if (!primary) return false;

  // Primary must be active
  if (!primary.active) return false;

  return true;
}

/**
 * Has Insurance Guard
 * Checks if patient has insurance on file
 */
export const hasInsurance: GuardWithMetadata = {
  impl: ({ event }) => {
    if (!event.output || !isResult(event.output)) return false;
    if (event.output.isErr()) return false;

    const data = event.output.value as { hasInsurance?: boolean };
    return data.hasInsurance === true;
  },
  metadata: {
    displayName: "hasInsurance",
    description: "Patient has insurance on file",
    requiresActor: "lookupInsurance",
    category: "success",
  },
};

/**
 * No Insurance Guard
 * Checks if patient has no insurance
 */
export const noInsurance: GuardWithMetadata = {
  impl: ({ event }) => {
    if (!event.output || !isResult(event.output)) return false;
    if (event.output.isErr()) return false;

    const data = event.output.value as { hasInsurance?: boolean };
    return data.hasInsurance === false;
  },
  metadata: {
    displayName: "noInsurance",
    description: "Patient has no insurance on file",
    requiresActor: "lookupInsurance",
    category: "success",
  },
};

/**
 * Insurance Verified Guard
 * Checks if insurance is verified
 */
export const insuranceVerified: GuardWithMetadata = {
  impl: ({ event }) => {
    if (!event.output || !isResult(event.output)) return false;
    if (event.output.isErr()) return false;

    const data = event.output.value as {
      hasInsurance?: boolean;
      verified?: boolean;
    };
    return data.hasInsurance === true && data.verified === true;
  },
  metadata: {
    displayName: "insuranceVerified",
    description: "Insurance is verified and active",
    requiresActor: "lookupInsurance",
    category: "success",
  },
};

/**
 * Insurance Lookup Failed Guard
 */
export const insuranceLookupFailed: GuardWithMetadata = {
  impl: ({ event }) => {
    if (!event.output || !isResult(event.output)) return false;
    return event.output.isErr();
  },
  metadata: {
    displayName: "insuranceLookupFailed",
    description: "Insurance lookup failed",
    requiresActor: "lookupInsurance",
    category: "error",
  },
};

/**
 * Insurance Verification Failed Guard
 * Checks if insurance verification failed
 */
export const insuranceVerificationFailed: GuardWithMetadata = {
  impl: ({ event }) => {
    // Check if this is an error from verifyInsurance actor
    return event.type === "error.platform.verifyInsurance";
  },
  metadata: {
    displayName: "insuranceVerificationFailed",
    description: "Insurance verification failed or insurance inactive",
    requiresActor: "verifyInsurance",
    category: "error",
  },
};

/**
 * Has Insurance Context Guard
 * Checks if insurance data exists in context AND not in pending prices mode
 *
 * If pendingPricesMode is true, skip insurance creation and let admin handle it
 */
export const hasInsuranceInContext: GuardWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
  impl: ({ context }: { context: any }) => {
    if (!context.insurances || typeof context.insurances !== "object") {
      return false;
    }
    // Check if either primary or secondary insurance exists
    return !!(
      context.insurances.primaryInsurance ||
      context.insurances.secondaryInsurance
    );
  },
  metadata: {
    displayName: "hasInsuranceInContext",
    description:
      "User has provided insurance information and not in pending prices mode",
    requiresContext: ["insurances", "pendingPricesMode"],
    category: "context",
  },
};

/**
 * No Insurance In Context Guard
 * Checks if insurance data does NOT exist in context (user skipped)
 */
export const noInsuranceInContext: GuardWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
  impl: ({ context }: { context: any }) => {
    if (!context.insurances || typeof context.insurances !== "object") {
      return true;
    }
    // Return true if neither primary nor secondary insurance exists
    return !(
      context.insurances.primaryInsurance ||
      context.insurances.secondaryInsurance
    );
  },
  metadata: {
    displayName: "noInsuranceInContext",
    description: "User has NOT provided insurance information (skipped)",
    requiresContext: ["insurances"],
    category: "context",
  },
};

/**
 * Pricing Fetch Success Guard
 */
export const pricingFetchSuccess: GuardWithMetadata = {
  impl: ({ event }) => {
    return event.type === "done.invoke.fetchPricing";
  },
  metadata: {
    displayName: "pricingFetchSuccess",
    description: "Pricing fetch completed successfully",
    requiresActor: "fetchPricing",
    category: "success",
  },
};

/**
 * Pricing Fetch Failed Guard
 */
export const pricingFetchFailed: GuardWithMetadata = {
  impl: ({ event }) => {
    return event.type === "error.platform.fetchPricing";
  },
  metadata: {
    displayName: "pricingFetchFailed",
    description: "Pricing fetch failed",
    requiresActor: "fetchPricing",
    category: "error",
  },
};

/**
 * Insurance Already Verified Guard
 * Checks if insurance verification already exists in context and is active,
 * AND the insurance inputs haven't changed since last verification.
 */
export const isInsuranceAlreadyVerified: GuardWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
  impl: ({ context }: { context: any }) => {
    // Insurance must be verified AND inputs must not have changed
    return (
      isAtLeastPrimaryInsuranceActive(context) &&
      !hasInsuranceInputChanged(context)
    );
  },
  metadata: {
    displayName: "isInsuranceAlreadyVerified",
    description:
      "Insurance has already been verified, is active, and inputs haven't changed",
    requiresContext: ["insuranceStates", "insuranceInputSnapshot"],
    category: "context",
  },
};

/**
 * Insurance Needs Verification Guard
 * Checks if insurance exists but hasn't been verified yet
 */
export const needsInsuranceVerification: GuardWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
  impl: ({ context }: { context: any }) => {
    const hasInsurance =
      context.insurances &&
      typeof context.insurances === "object" &&
      (context.insurances.primaryInsurance ||
        context.insurances.secondaryInsurance);
    const isVerified = isAtLeastPrimaryInsuranceActive(context);
    return !!hasInsurance && !isVerified;
  },
  metadata: {
    displayName: "needsInsuranceVerification",
    description:
      "Insurance data exists but hasn't been verified or verification failed",
    requiresContext: ["insurances", "insuranceStates"],
    category: "context",
  },
};

/**
 * Extract insurance input fields from an insurance object.
 * These are user-entered fields that trigger re-verification when changed.
 */
function extractInsuranceInputFields(
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  insurance: any,
): Record<string, unknown> | null {
  if (!insurance) return null;
  return {
    payer: insurance.payer,
    payerId: insurance.payerId,
    memberId: insurance.memberId,
    state: insurance.state,
    ehrInsuranceId: insurance.ehrInsuranceId,
  };
}

/**
 * Helper to create a snapshot of insurance INPUT fields only.
 * These are the user-entered fields that, if changed, require re-verification.
 * Does NOT include verification outputs (status, insuranceId, insuranceStates).
 */
// biome-ignore lint/suspicious/noExplicitAny: Context type is complex and varies
export function createInsuranceInputSnapshot(context: any): string {
  const snapshot = {
    primaryInsurance: extractInsuranceInputFields(
      context.insurances?.primaryInsurance,
    ),
    secondaryInsurance: extractInsuranceInputFields(
      context.insurances?.secondaryInsurance,
    ),
  };
  return JSON.stringify(snapshot);
}

/**
 * Helper to check if insurance inputs have changed since last verification.
 * Returns true if inputs have changed (need re-verification).
 */
// biome-ignore lint/suspicious/noExplicitAny: Context type is complex and varies
export function hasInsuranceInputChanged(context: any): boolean {
  // If no snapshot exists, we haven't verified yet - treat as "changed"
  if (!context.insuranceInputSnapshot) return true;

  const currentSnapshot = createInsuranceInputSnapshot(context);
  return context.insuranceInputSnapshot !== currentSnapshot;
}

/**
 * Helper to check if insurance inputs have changed, accounting for incoming event data.
 * This is needed because guards run BEFORE actions, so context hasn't been updated yet
 * with the new form data from the event.
 *
 * Returns true if inputs have changed (need re-verification).
 */
export function hasInsuranceInputChangedWithEvent(
  // biome-ignore lint/suspicious/noExplicitAny: Context type is complex and varies
  context: any,
  // biome-ignore lint/suspicious/noExplicitAny: Event type varies by transition
  event: any,
): boolean {
  // If no snapshot exists, we haven't verified yet - treat as "changed"
  if (!context.insuranceInputSnapshot) return true;

  // Simulate what context.insurances will look like AFTER saveFormData runs
  const eventData = event?.data || {};
  const mergedInsurances = eventData.insurances
    ? {
        primaryInsurance: eventData.insurances.primaryInsurance
          ? {
              ...context.insurances?.primaryInsurance,
              ...eventData.insurances.primaryInsurance,
            }
          : context.insurances?.primaryInsurance,
        secondaryInsurance: eventData.insurances.secondaryInsurance
          ? {
              ...context.insurances?.secondaryInsurance,
              ...eventData.insurances.secondaryInsurance,
            }
          : context.insurances?.secondaryInsurance,
      }
    : context.insurances;

  // Create snapshot from merged data (what it will look like after saveFormData)
  const futureSnapshot = {
    primaryInsurance: extractInsuranceInputFields(
      mergedInsurances?.primaryInsurance,
    ),
    secondaryInsurance: extractInsuranceInputFields(
      mergedInsurances?.secondaryInsurance,
    ),
  };
  const futureSnapshotString = JSON.stringify(futureSnapshot);

  return context.insuranceInputSnapshot !== futureSnapshotString;
}

/**
 * Extract all insurance fields relevant to pricing from an insurance object.
 * Includes both input fields and verification outputs that affect pricing.
 */
function extractInsurancePricingFields(
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  insurance: any,
): Record<string, unknown> | null {
  if (!insurance) return null;
  return {
    ...extractInsuranceInputFields(insurance),
    status: insurance.status,
    insuranceId: insurance.insuranceId,
    ehrInsurancePackageId: insurance.ehrInsurancePackageId,
  };
}

/**
 * Helper to create a snapshot of pricing-relevant inputs for comparison.
 * Any change to these fields should trigger repricing.
 */
// biome-ignore lint/suspicious/noExplicitAny: Context type is complex and varies
function createPricingInputSnapshot(context: any): string {
  const snapshot = {
    primaryInsurance: extractInsurancePricingFields(
      context.insurances?.primaryInsurance,
    ),
    secondaryInsurance: extractInsurancePricingFields(
      context.insurances?.secondaryInsurance,
    ),
    insuranceStates: context.insuranceStates,
    selectedHrtId: context.selectedHrt?.hrtId,
    defaultHrtId: context.defaultHrt?.hrtId,
    pendingPricesMode: context.pendingPricesMode,
  };
  return JSON.stringify(snapshot);
}

/**
 * Has Pricing Data Guard
 * Checks if pricing data already exists in context AND inputs haven't changed
 *
 * Compares current pricing-relevant inputs against the snapshot taken when
 * pricing was fetched. If inputs have changed, returns false to trigger repricing.
 */
export const hasPricingData: GuardWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
  impl: ({ context }: { context: any }) => {
    const pricingMap = context.pricingMap;
    if (!pricingMap || typeof pricingMap !== "object") return false;

    // Check if pricingMap has at least one entry
    if (Object.keys(pricingMap).length === 0) return false;

    // If no snapshot exists, pricing is stale (from before this feature)
    if (!context.pricingInputSnapshot) return false;

    // Compare current inputs against snapshot
    const currentSnapshot = createPricingInputSnapshot(context);
    return context.pricingInputSnapshot === currentSnapshot;
  },
  metadata: {
    displayName: "hasPricingData",
    description:
      "Pricing data exists and inputs haven't changed since last fetch",
    requiresContext: ["pricingMap", "pricingInputSnapshot"],
    category: "context",
  },
};

// Export helper for use in machine actions
export { createPricingInputSnapshot };

/**
 * Helper function to check if all insured HRTs have pricing data
 * Shared between guard and machine logic
 */
export function checkHasInsurancePricingData(context): boolean {
  const { pricingMap, hrts } = context;

  if (!pricingMap || typeof pricingMap !== "object") return false;

  if (!hrts || !Array.isArray(hrts)) return false;

  // Find all HRTs that support INSURED payment type
  const insuredHrts = hrts.filter((hrt: HrtType) => {
    return hrt.hrtPaymentType?.some(
      (pt: HrtType["hrtPaymentType"][number]) => pt.paymentType === "INSURED",
    );
  });

  // If no HRTs support insurance, return false
  if (insuredHrts.length === 0) return false;

  // Check that each insured HRT has corresponding INSURED pricing data
  return insuredHrts.every((hrt: HrtType) => {
    const hrtId = hrt.hrtId;
    const hrtPricing = pricingMap[hrtId];

    return (
      hrtPricing?.priceData?.INSURED &&
      typeof hrtPricing.priceData.INSURED === "object"
    );
  });
}

/**
 * Has Insurance Pricing Data Guard
 * Checks if all HRTs with INSURED payment type have corresponding INSURED pricing data
 */
export const hasInsurancePricingData: GuardWithMetadata = {
  impl: ({ context }: { context }) => {
    return checkHasInsurancePricingData(context);
  },
  metadata: {
    displayName: "hasInsurancePricingData",
    description:
      "All HRTs with INSURED payment type have corresponding INSURED pricing data in pricingMap",
    requiresContext: ["pricingMap", "hrts"],
    category: "context",
  },
};
